import { useEffect, useState } from "react";
import useCrmApi from "../../../components/hook/crm-req";

// Nigerian banking profiles mock — see
// docs/nigerian-banking-profiles-mock-ui-integration.md. Read-only,
// authenticated, 250 deterministic synthetic profiles under
// /api/v1/traits/customers. This endpoint is dedicated to the Profile
// Explorer; the trait builder's sample-data preview (../traits/api.ts)
// borrows a small slice of the same list rather than owning its own types.
// Every record is synthetic demo data — never a real customer, never used
// for contact, identity checks, credit decisions, or production analytics.

export type ProfileRiskLevel = "low" | "medium" | "high";
export type NigerianNetwork = "MTN" | "Airtel" | "Glo" | "9mobile";
export type ProfileTagTone = "positive" | "neutral" | "warning" | "danger";

export interface MockProfileTag {
  key: string;
  label: string;
  tone: ProfileTagTone;
}

export interface MockCustomerProfileSummary {
  customer_id: string;
  display_id: string;
  name: string;
  msisdn: string;
  email: string;
  network: NigerianNetwork;
  region: string;
  country: "Nigeria";
  currency: "NGN";
  risk_level: ProfileRiskLevel;
  balance_tier: string;
  engagement_score: number;
  tags: MockProfileTag[];
  latest_event_at: string;
  is_synthetic: true;
}

export interface MockCustomerProfileListResponse {
  data: MockCustomerProfileSummary[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface MockCustomerOverview {
  engagement_score: number;
  region: string;
  age_band: string;
  balance_tier: string;
  latest_event: string;
}

export interface MockCustomerTraitValue {
  key: string;
  label: string;
  value: unknown;
  source: string;
}

export interface MockCustomerTraitGroup {
  key: "declared" | "behavioral" | "predictive";
  label: string;
  source_badge: string;
  values: MockCustomerTraitValue[];
}

export interface MockCustomerEvent {
  id: string;
  type: string;
  label: string;
  source: "CORE_BANKING" | "NIP" | "CARD" | "DIGITAL" | "KYC" | string;
  occurred_at: string;
  description: string;
  status: string;
  amount: number | null;
  currency: "NGN" | null;
}

export interface MockCustomerExternalId {
  type: "msisdn" | "customer_id" | "account_number" | "bvn_hash" | "email";
  label: string;
  value: string;
  is_primary: boolean;
  is_masked: boolean;
}

export interface MockCustomerProfile {
  customer_id: string;
  display_id: string;
  name: string;
  msisdn: string;
  email: string;
  network: NigerianNetwork;
  country: "Nigeria";
  currency: "NGN";
  risk_level: ProfileRiskLevel;
  tags: MockProfileTag[];
  overview: MockCustomerOverview;
  attributes: Record<string, unknown>;
  traits: Record<string, unknown>;
  trait_groups: MockCustomerTraitGroup[];
  events: MockCustomerEvent[];
  external_ids: MockCustomerExternalId[];
  segments: string[];
  generated_at: string;
  is_synthetic: true;
}

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("authToken")}` });

const errorFor = (status: number, fallback: string): string => {
  if (status === 403) return "You don't have access to view this.";
  if (status === 422) return "Those filters aren't supported.";
  return fallback;
};

export function formatEventAmount(event: MockCustomerEvent): string | null {
  if (event.amount === null || event.currency === null) return null;
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: event.currency,
    maximumFractionDigits: 2,
  }).format(event.amount);
}

export function formatProfileValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export function formatProfileTimestamp(value: string): string {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

// ─── List: search / region / network / risk filters, pagination ───────────
export interface ProfileListFilters {
  search?: string;
  region?: string;
  network?: NigerianNetwork;
  riskLevel?: ProfileRiskLevel;
  page: number;
  perPage: number;
}

export function useProfilesList(filters: ProfileListFilters) {
  const { makeRequest } = useCrmApi("/traits/customers", "GET", authHeaders());
  const [data, setData] = useState<MockCustomerProfileListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      const params: Record<string, any> = { page: filters.page, per_page: filters.perPage };
      if (filters.search?.trim()) params.search = filters.search.trim().slice(0, 100);
      if (filters.region) params.region = filters.region;
      if (filters.network) params.network = filters.network;
      if (filters.riskLevel) params.risk_level = filters.riskLevel;
      const [json, status] = await makeRequest(undefined, params);
      if (cancelled) return;
      setLoading(false);
      if (status >= 200 && status < 300) setData(json);
      else setError(errorFor(status, "Couldn't load profiles."));
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.region, filters.network, filters.riskLevel, filters.page, filters.perPage, reloadKey]);

  return { data, loading, error, retry: () => setReloadKey((k) => k + 1) };
}

// ─── Selected profile detail — all four tabs in one request ────────────────
export function useProfileDetail(customerId: string | null) {
  const { makeRequest } = useCrmApi("/traits/customers", "GET", authHeaders());
  const [data, setData] = useState<MockCustomerProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!customerId) {
      setData(null);
      setNotFound(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setError(null);
    (async () => {
      const [json, status] = await makeRequest(undefined, undefined, customerId);
      if (cancelled) return;
      setLoading(false);
      if (status === 200) setData(json);
      else if (status === 404) setNotFound(true);
      else setError(errorFor(status, "Couldn't load this profile."));
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  return { data, loading, notFound, error };
}
